import * as React from "react";
import { Hint } from "react-ui";

import { UNIT_MENU_CLICK } from "../../metricsAction";
import { isChildOfType, nullElement } from "../../TypingUtils";
import { UnitId, unitIdToLegacyUnitId } from "../Unit/UnitId/UnitId";
import { reject } from "../../../../../Common/TypingUtils";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { RegisterDomElement } from "../../Commons/DOM/DomElementsRegistry";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { UnitRemoveConfirmModal } from "../Unit/Modal/UnitRemoveConfirmModal";
import { ChildPage, UnitClearConfirmationSettings } from "../Unit/Unit";
import { UnitClearConfirmModal } from "../Unit/Modal/UnitClearConfirmModal";
import { ClearPageMethod } from "../../../Engine/ActionCreators/UnitActionCreators";
import { PropsPageActions } from "../Unit/Types";

import { UnitItemContainer } from "./UnitItemContainer";
import { MenuItem } from "./Internals/MenuItem/MenuItem";
import { MultipleUnitContainer as MultipleUnit } from "./Internals/MultipleUnit/MultipleUnitContainer";
import { NestedLevelContext } from "./NestedLevelContext";
import { NestedItems } from "./Internals/MultipleUnit/NestedItems.styles";
import { IconButton } from "./Internals/IconButton/IconButton";
import { MenuKebab } from "./MenuKebab";

export interface UnitItemProps {
    id?: UnitId;
    childUnitIds: UnitId[];
    name?: string;
    multiple: boolean;
    unitPath?: AbsoluteResolvedModelPath;
    optional?: boolean;
    hidden: boolean;
    filled: boolean;
    pages?: ChildPage[];
    clearConfirmationSettings?: UnitClearConfirmationSettings;
    children?: React.ReactNode;
    addingHint?: string;
    isCurrent: boolean;
    isChildItemActive: boolean;
    hasVisibleChildren: boolean;
    navigationLimit: undefined | number;
    maxOccurs?: number;
    hideActions: boolean;
    pageActions?: PropsPageActions;
    emptyUnitsVisible: boolean;
    onAddMultipleUnitInstance: (bindingPath: AbsoluteResolvedModelPath, id: UnitId) => void;
    onRemoveMultipleUnitInstance: (bindingPath: AbsoluteResolvedModelPath, instance: string, id: UnitId) => void;
    onCopyMultipleUnitInstance: (bindingPath: AbsoluteResolvedModelPath, instance: string, id: UnitId) => Promise<void>;
    onScrollToUnit: (unitId: UnitId) => void;
    isOptionalAndEmpty: () => boolean;
    onClearMultipleUnit: (instanceId: string, method: ClearPageMethod) => void;
    onClearPageSingleUnit: (method: ClearPageMethod) => void;
    isReadOnly?: boolean;
    currentInstance: undefined | string;
    onRemoveSingleUnit: (path: AbsoluteResolvedModelPath) => void;
    onAddSingleUnit: () => void;
    isOnlyPlus: boolean;
}

export function UnitItem(props: UnitItemProps): JSX.Element | null {
    const track = useMetrics();
    const nestedLevel = React.useContext(NestedLevelContext);
    const [currentRemovingInstance, setCurrentRemovingInstance] = React.useState<string | null>(null);
    const [currentRemovingInstanceTitle, setCurrentRemovingInstanceTitle] = React.useState<string | null>(null);
    const [currentRemovingInstanceSingle, setCurrentRemovingInstanceSingle] = React.useState<string | null>(null);

    const [currentClearingInstance, setCurrentClearingInstance] = React.useState<string | null>(null);
    const [currentClearingInstanceTitle, setCurrentClearingInstanceTitle] = React.useState<string | null>(null);
    const [currentClearingInstanceSingle, setCurrentClearingInstanceSingle] = React.useState<string | null>(null);

    const { hidden, multiple } = props;
    const hasChildPages = props.pages != undefined ? props.pages.length > 0 : false;

    const handleAddInstance = (): void => {
        if (props.unitPath && props.id != undefined) {
            props.onAddMultipleUnitInstance(props.unitPath, props.id);
        }
    };
    const handleAddSingleInstance = (): void => {
        if (props.unitPath && props.id != undefined) {
            scrollToUnit(props.id);
            props.onAddSingleUnit();
        }
    };

    const handleClickMainItem = (): void => {
        track(UNIT_MENU_CLICK, { unit: unitIdToLegacyUnitId(props.id) });
        if (props.id != undefined) {
            scrollToUnit(props.id);
        } else {
            const child = props.children != undefined ? React.Children.toArray(props.children)[0] : undefined;
            if (child != undefined && isChildOfType(child, UnitItemContainer) && child.props.id != undefined) {
                props.onScrollToUnit(child.props.id);
            }
        }
    };

    const callRemoveConfirmModal = (instance: string, instanceTitle: string) => {
        setCurrentRemovingInstance(instance);
        setCurrentRemovingInstanceTitle(instanceTitle);
    };
    const callRemoveConfirmModalSingle = (instance: string) => {
        setCurrentRemovingInstanceSingle(instance);
    };
    const callClearConfirmModal = (instance: string, instanceTitle: string) => {
        setCurrentClearingInstance(instance);
        setCurrentClearingInstanceTitle(instanceTitle);
    };
    const callClearConfirmModalSingle = (instance: string) => {
        setCurrentClearingInstanceSingle(instance);
    };

    const handleRemoveInstance = (): void => {
        if (currentRemovingInstance !== null && props.unitPath && props.id != undefined) {
            props.onRemoveMultipleUnitInstance(props.unitPath, currentRemovingInstance, props.id);
            setCurrentRemovingInstance(null);
        }
    };

    const handleRemoveInstanceSingle = (): void => {
        if (props.onRemoveSingleUnit && props.unitPath !== undefined) {
            props.onRemoveSingleUnit(props.unitPath);
            setCurrentRemovingInstanceSingle(null);
        }
    };

    const handleClearInstance = (method: ClearPageMethod): void => {
        if (currentClearingInstance !== null) {
            props.onClearMultipleUnit(currentClearingInstance, method);
            setCurrentClearingInstance(null);
        }
    };
    const handleClearInstanceSingle = (method: ClearPageMethod): void => {
        if (currentClearingInstanceSingle !== null) {
            props.onClearPageSingleUnit(method);
            setCurrentClearingInstanceSingle(null);
        }
    };

    const handleCopyInstance = async (instance: string): Promise<void> => {
        if (props.unitPath && props.id != undefined) {
            await props.onCopyMultipleUnitInstance(props.unitPath, instance, props.id);
        }
    };

    const handleGoToUnit = (unitId: UnitId): void => {
        scrollToUnit(unitId);
    };

    const scrollToUnit = (id: UnitId): void => {
        props.onScrollToUnit(id);
    };

    if (hidden) {
        return nullElement;
    }

    function renderSingle(): React.ReactElement {
        const isRenderActions = !props.isReadOnly && !/title/i.test(props.id?.pageId ?? "") && !props.hideActions;
        const getActions = () => {
            if (!isRenderActions || props.isOnlyPlus) {
                return null;
            }
            if (props.filled) {
                return (
                    <MenuKebab
                        data-page-id={props.id?.pageId}
                        pageActions={props.pageActions}
                        onClearInstance={() => callClearConfirmModalSingle("0")}
                        onRemoveInstance={props.optional ? () => callRemoveConfirmModalSingle("0") : undefined}
                    />
                );
            } else {
                return (
                    <Hint useWrapper={false} pos="bottom left" text={"Заполнить"}>
                        <IconButton
                            data-tid="Add"
                            data-page-id={props.id?.pageId}
                            icon="PlusIcon"
                            onClick={handleAddSingleInstance}
                        />
                    </Hint>
                );
            }
        };

        return (
            <MenuItem
                id={props.id}
                onClick={handleClickMainItem}
                empty={!(props.isOptionalAndEmpty() && props.filled)}
                active={props.isCurrent}
                childActive={props.isChildItemActive}
                name={props.name}
                action={getActions()}
                fufDraftId={""}>
                {props.children && props.filled && (
                    <NestedLevelContext.Provider value={nestedLevel + 1}>
                        <NestedItems>{props.children}</NestedItems>
                    </NestedLevelContext.Provider>
                )}
            </MenuItem>
        );
    }

    function renderMultiple(): null | JSX.Element {
        if (props.id == undefined) {
            return nullElement;
        }

        const navigationLimit = props.navigationLimit ?? 15;

        return (
            <MultipleUnit
                unitPath={props.unitPath ?? reject("Require unitPath")}
                id={props.id}
                name={props.name}
                optional={props.optional}
                hideActions={props.hideActions}
                pageActions={props.pageActions}
                navigationLimit={navigationLimit}
                maxOccurs={props.maxOccurs}
                onGoToUnit={handleGoToUnit}
                onAddInstance={handleAddInstance}
                onRemoveInstance={callRemoveConfirmModal}
                onClearInstance={callClearConfirmModal}
                childUnitIds={props.childUnitIds}
                onCopyInstance={handleCopyInstance}
                isOnlyPlus={props.isOnlyPlus}>
                {props.children}
            </MultipleUnit>
        );
    }

    return (
        <RegisterDomElement id={unitIdToLegacyUnitId(props.id)}>
            {currentRemovingInstance !== null && (
                <div onClick={e => e.stopPropagation()}>
                    <UnitRemoveConfirmModal
                        name={`${props.name} ${currentRemovingInstanceTitle}`}
                        hasChildren={hasChildPages}
                        removeChildrenText={props.clearConfirmationSettings?.removeChildrenText}
                        onRemove={handleRemoveInstance}
                        onClose={() => setCurrentRemovingInstance(null)}
                    />
                </div>
            )}
            {currentRemovingInstanceSingle !== null && (
                <div onClick={e => e.stopPropagation()}>
                    <UnitRemoveConfirmModal
                        name={props.name}
                        hasChildren={hasChildPages}
                        removeChildrenText={props.clearConfirmationSettings?.removeChildrenText}
                        onRemove={handleRemoveInstanceSingle}
                        onClose={() => setCurrentRemovingInstanceSingle(null)}
                    />
                </div>
            )}
            {currentClearingInstance !== null && (
                <div onClick={e => e.stopPropagation()}>
                    <UnitClearConfirmModal
                        name={`${props.name} ${currentClearingInstanceTitle}`}
                        hasChildren={hasChildPages}
                        onClear={handleClearInstance}
                        onClose={() => setCurrentClearingInstance(null)}
                    />
                </div>
            )}
            {currentClearingInstanceSingle !== null && (
                <div onClick={e => e.stopPropagation()}>
                    <UnitClearConfirmModal
                        name={props.name}
                        hasChildren={hasChildPages}
                        onClear={handleClearInstanceSingle}
                        onClose={() => setCurrentClearingInstanceSingle(null)}
                    />
                </div>
            )}
            {multiple ? renderMultiple() : renderSingle()}
        </RegisterDomElement>
    );
}
